import * as Sentry from "@sentry/react";

/**
 * Get the current user's email from localStorage (if logged in)
 */
const getUserEmail = () => {
  return localStorage.getItem("userEmail") || null;
};

/**
 * Set user context in Sentry
 */
const setUserContext = () => {
  const email = getUserEmail();
  if (email) {
    Sentry.setUser({ email });
  }
};

/**
 * Capture an error and send to Sentry (for critical errors)
 * Use this for errors that should ALWAYS be tracked
 * 
 * @param {Error} error - The error object
 * @param {string} context - Description of what was happening (e.g., "fetching chats")
 * @param {object} extra - Additional data to include
 */
export const captureError = (error, context, extra = {}) => {
  console.error(`${context}:`, error);
  
  setUserContext();
  
  Sentry.captureException(error, {
    tags: {
      context,
    },
    extra: {
      ...extra,
      userEmail: getUserEmail(),
      errorResponse: error.response?.data,
      errorStatus: error.response?.status,
    },
  });
};

/**
 * Capture an error only if it's a server error (5xx)
 * Use this for errors that might be user errors (wrong password, etc.)
 * but should be tracked if it's actually a server issue
 * 
 * @param {Error} error - The error object
 * @param {string} context - Description of what was happening
 * @param {object} extra - Additional data to include
 */
export const captureServerError = (error, context, extra = {}) => {
  console.error(`${context}:`, error);
  
  const status = error.response?.status;
  
  // Only send to Sentry if it's a server error (5xx) or network error (no status)
  if (!status || status >= 500) {
    setUserContext();
    
    Sentry.captureException(error, {
      tags: {
        context,
        errorType: status ? "server_error" : "network_error",
      },
      extra: {
        ...extra,
        userEmail: getUserEmail(),
        errorResponse: error.response?.data,
        errorStatus: status,
      },
    });
  }
};

